import React from 'react'
import axios from 'axios'
import Select from 'react-select'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import LinearProgress from '@material-ui/core/LinearProgress'
import { Button, CircularProgress } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import VisibilityIcon from '@material-ui/icons/Visibility'
import WarningIcon from '@material-ui/icons/Warning'
import CloseIcon from '@material-ui/icons/Close'
import { GET_UPLOAD_URL, GET_FILE_FORMAT, GET_SOLUTION_TYPES, GET_INSTALLATION_IMAGE_HEADERS } from './queries'
import {
  buildImageModel,
  collectEntries,
  headersForSolution,
  newRowId,
  normaliseKey,
  solutionId,
  solutionLabel
} from './installationImageUtils'

const BUCKET_NAME = 'aquila-track-uploads'
const MAX_FILE_SIZE = 30 * 1024 * 1024
const ALLOWED_TYPES = ['jpeg', 'jpg', 'png', 'pdf']

const styles = {
  block: { border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 14 },
  blockHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #f2f2f2' },
  headerCell: { flex: '0 0 42%', fontSize: 13 },
  fileCell: { flex: 1, minWidth: 0, fontSize: 12 },
  fileName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
  mandatory: { color: '#d32f2f', marginLeft: 2 },
  warning: { color: '#b26a00', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 },
  error: { color: '#d32f2f', fontSize: 12 },
  muted: { color: '#757575', fontSize: 12 }
}

const InstallationImagesModal = ({ open, onClose, onSave, client, entries, vehicleSolutionType, mode }) => {
  const [loading, setLoading] = React.useState(false)
  const [headersBySolution, setHeadersBySolution] = React.useState({})
  const [solutionOptions, setSolutionOptions] = React.useState([])
  const [solutionNames, setSolutionNames] = React.useState({})
  const [blocks, setBlocks] = React.useState([])
  const [unassigned, setUnassigned] = React.useState([])
  const [newSolution, setNewSolution] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [submitted, setSubmitted] = React.useState(false)
  const [preview, setPreview] = React.useState(null) // { fileName, url, loading, error }
  const previewUrls = React.useRef({}) // fileName -> signed URL, reused while the modal is open

  // Load header master + solution list, then build the model from the current entries.
  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setMessage('')
      setSubmitted(false)
      const bySolution = {}
      const names = {}
      const solutions = []
      const addSolution = s => {
        if (s && solutions.indexOf(s) === -1) solutions.push(s)
      }
      // Solution types come only from solution_type_master (same API as the other solution dropdowns).
      const [masterResult, headerResult] = await Promise.all([
        client.query({ query: GET_SOLUTION_TYPES }).catch(e => {
          console.error('Could not load solution types', e)
          return null
        }),
        client.query({ query: GET_INSTALLATION_IMAGE_HEADERS, fetchPolicy: 'network-only' }).catch(e => {
          console.error('Could not load installation image headers', e)
          return null
        })
      ])
      ;((masterResult && masterResult.data && masterResult.data.getAllMasterSolutionType) || []).forEach(st => {
        const id = solutionId(st.id)
        if (!id) return
        names[id] = st.solutionTypeName || id
        addSolution(id)
      })
      ;((headerResult && headerResult.data && headerResult.data.getInstallationImageHeaders) || []).forEach(h => {
        const id = solutionId(h.solutionType)
        if (!id) return
        if (!bySolution[id]) bySolution[id] = []
        bySolution[id].push(h)
      })
      Object.keys(bySolution).forEach(s => bySolution[s].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)))
      if (cancelled) return
      const model = buildImageModel(entries || [], vehicleSolutionType, bySolution, mode, solutions.length ? solutions : null)
      setHeadersBySolution(bySolution)
      setSolutionNames(names)
      setSolutionOptions(solutions)
      setBlocks(model.blocks)
      setUnassigned(model.unassigned)
      setNewSolution('')
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const updateRow = (blockId, rowId, patch) =>
    setBlocks(prev =>
      prev.map(b => (b.id !== blockId ? b : { ...b, rows: b.rows.map(r => (r.id !== rowId ? r : { ...r, ...patch })) }))
    )

  const handleUpload = async (blockId, rowId, file) => {
    if (!file) return
    const ext = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
    if (ALLOWED_TYPES.indexOf(ext) === -1) {
      updateRow(blockId, rowId, { error: 'Only JPEG, JPG, PNG or PDF files are allowed' })
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      updateRow(blockId, rowId, { error: 'File size exceeds the limit of 30MB' })
      return
    }
    updateRow(blockId, rowId, { uploading: true, progress: 0, error: '' })
    try {
      const response = await client.mutate({ mutation: GET_UPLOAD_URL, variables: { fileExtension: ext } })
      const { publicUploadURL, filename } = response.data.getPublicUploadURL
      await axios.put(publicUploadURL, file, {
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress: e => updateRow(blockId, rowId, { progress: e.total ? (e.loaded / e.total) * 100 : 0 })
      })
      updateRow(blockId, rowId, { uploading: false, fileName: filename, original: null, dirty: true, error: '' })
    } catch (e) {
      console.error('Upload error:', e)
      updateRow(blockId, rowId, { uploading: false, error: 'Upload failed, please try again' })
    }
  }

  // Opens the image in a preview dialog on top of this modal (no new tab).
  const handleView = async fileName => {
    const cached = previewUrls.current[fileName]
    if (cached) {
      setPreview({ fileName, url: cached, loading: false, error: '' })
      return
    }
    setPreview({ fileName, url: '', loading: true, error: '' })
    try {
      const { data } = await client.query({
        query: GET_FILE_FORMAT,
        variables: { bucketname: BUCKET_NAME, filename: fileName },
        errorPolicy: 'all'
      })
      const url = data && data.getPublicDownloadURL
      if (url) previewUrls.current[fileName] = url
      setPreview(prev =>
        prev && prev.fileName === fileName
          ? { fileName, url: url || '', loading: false, error: url ? '' : 'Image not available' }
          : prev
      )
    } catch (e) {
      setPreview(prev => (prev && prev.fileName === fileName ? { fileName, url: '', loading: false, error: 'Image not available' } : prev))
    }
  }

  const addSolutionBlock = () => {
    if (!newSolution || blocks.some(b => b.solution === newSolution)) return
    const headers = headersForSolution(headersBySolution, newSolution)
    const mandatory = headers.filter(h => h.isMandatory)
    // A solution with no checklist needs at least one image, offered as "Other".
    const rows = (mandatory.length ? mandatory : headers.filter(h => normaliseKey(h.headerName) === 'other')).map(
      header => ({ id: newRowId(), header, fileName: '', original: null, dirty: false })
    )
    setBlocks(prev => [...prev, { id: newRowId(), solution: newSolution, isNew: true, rows }])
    setNewSolution('')
  }

  const removeBlock = block => {
    const hasStored = block.rows.some(r => r.original)
    if (hasStored && !window.confirm(`Remove ${solutionLabel(block.solution, solutionNames)} and its existing images from this vehicle?`)) return
    setBlocks(prev => prev.filter(b => b.id !== block.id))
  }

  const addOptionalRow = block => {
    const optional = headersForSolution(headersBySolution, block.solution).filter(h => !h.isMandatory)
    setBlocks(prev =>
      prev.map(b =>
        b.id !== block.id
          ? b
          : { ...b, rows: [...b.rows, { id: newRowId(), header: optional[optional.length - 1] || null, fileName: '', original: null, dirty: false }] }
      )
    )
  }

  const removeRow = (blockId, row) => {
    if (row.original && !window.confirm('Remove this existing image from the vehicle?')) return
    setBlocks(prev => prev.map(b => (b.id !== blockId ? b : { ...b, rows: b.rows.filter(r => r.id !== row.id) })))
  }

  const sameHeader = (a, b) => !!a && !!b && normaliseKey(a.headerName) === normaliseKey(b.headerName)

  // A mandatory header holds one image; it counts as taken once any row of it has (or is uploading) a file.
  const isMandatoryTaken = (block, header) =>
    header.isMandatory && block.rows.some(r => (r.fileName || r.uploading) && sameHeader(r.header, header))

  // Headers an unassigned image may go to: every optional header (repeatable) + mandatory ones still empty.
  const assignableHeaders = block =>
    headersForSolution(headersBySolution, block.solution).filter(h => !isMandatoryTaken(block, h))

  const assignUnassigned = item => {
    const block = blocks.find(b => b.solution === item.solution)
    if (!block) return
    const header = assignableHeaders(block).find(h => h.headerName === item.headerName)
    if (!header) {
      setMessage(`"${item.headerName}" already has an image. Choose another header.`)
      return
    }
    const assigned = { header, fileName: item.entry.fileName, original: item.entry, dirty: true }
    setBlocks(prev =>
      prev.map(b => {
        if (b.id !== block.id) return b
        // Mandatory: fill its auto-created empty slot instead of adding a duplicate row.
        const emptySlot = header.isMandatory && b.rows.find(r => !r.fileName && !r.uploading && sameHeader(r.header, header))
        return emptySlot
          ? { ...b, rows: b.rows.map(r => (r.id === emptySlot.id ? { ...r, ...assigned, error: '' } : r)) }
          : { ...b, rows: [...b.rows, { id: newRowId(), ...assigned }] }
      })
    )
    setUnassigned(prev => prev.filter(u => u.id !== item.id))
    setMessage('')
  }

  const blockProblems = block => {
    const headers = headersForSolution(headersBySolution, block.solution)
    const hasChecklist = headers.some(h => h.isMandatory)
    const missing = block.rows.filter(r => r.header && r.header.isMandatory && !r.fileName).map(r => r.header.headerName)
    const uniqueMissing = missing.filter((m, i) => missing.indexOf(m) === i)
    // a mandatory header with an image in another row of the same block is satisfied
    const filledHeaders = block.rows.filter(r => r.fileName && r.header).map(r => r.header.headerName)
    const stillMissing = uniqueMissing.filter(m => filledHeaders.indexOf(m) === -1)
    const noImage = !hasChecklist && !block.rows.some(r => r.fileName)
    return { stillMissing, noImage }
  }

  const isUploading = blocks.some(b => b.rows.some(r => r.uploading))
  const strictBlocks = blocks.filter(b => mode === 'add' || b.isNew)
  const blockingErrors = strictBlocks.filter(b => {
    const p = blockProblems(b)
    return p.stillMissing.length > 0 || p.noImage
  })

  const handleSave = () => {
    setSubmitted(true)
    if (isUploading) {
      setMessage('Please wait for uploads to finish.')
      return
    }
    if (blockingErrors.length) {
      setMessage('Please upload all mandatory images for the highlighted solution types.')
      return
    }
    onSave(collectEntries(blocks, unassigned))
  }

  const solutionSelectOptions = solutionOptions
    .filter(s => !blocks.some(b => b.solution === s))
    .map(s => ({ value: s, label: solutionLabel(s, solutionNames) }))

  const renderFileCell = (block, row) => (
    <div style={styles.fileCell}>
      {row.uploading ? (
        <LinearProgress variant="determinate" value={row.progress || 0} />
      ) : row.fileName ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={styles.fileName} title={row.fileName}>
            {row.fileName}
          </span>
          <VisibilityIcon style={{ fontSize: 18, cursor: 'pointer', color: '#1976d2' }} titleAccess="View" onClick={() => handleView(row.fileName)} />
          <Button size="small" component="label" style={{ minWidth: 0, padding: '0 6px', fontSize: 11 }}>
            Replace
            <input type="file" hidden accept=".jpeg, .jpg, .png, .pdf, image/jpeg, image/png, application/pdf" onChange={e => handleUpload(block.id, row.id, e.target.files[0])} />
          </Button>
        </div>
      ) : (
        <Button size="small" variant="outlined" color="primary" component="label" style={{ fontSize: 11 }}>
          <CloudUploadIcon style={{ fontSize: 16, marginRight: 4 }} />
          Choose image
          <input type="file" hidden accept=".jpeg, .jpg, .png, .pdf, image/jpeg, image/png, application/pdf" onChange={e => handleUpload(block.id, row.id, e.target.files[0])} />
        </Button>
      )}
      {row.error && <div style={styles.error}>{row.error}</div>}
    </div>
  )

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Installation Images</DialogTitle>
      <DialogContent style={{ minHeight: 360 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress />
          </div>
        ) : (
          <React.Fragment>
            <div style={{ ...styles.muted, marginBottom: 12 }}>
              Select a solution type: its mandatory images are listed automatically. Use "Add more images" for optional headers. JPEG, JPG, PNG or PDF up to 30 MB.
            </div>

            {blocks.map(block => {
              const problems = blockProblems(block)
              const strict = mode === 'add' || block.isNew
              const showError = strict && submitted && (problems.stillMissing.length > 0 || problems.noImage)
              const optionalHeaders = headersForSolution(headersBySolution, block.solution).filter(h => !h.isMandatory)
              return (
                <div key={block.id} style={{ ...styles.block, borderColor: showError ? '#d32f2f' : '#e0e0e0' }}>
                  <div style={styles.blockHead}>
                    <strong>{solutionLabel(block.solution, solutionNames)}</strong>
                    <DeleteIcon style={{ cursor: 'pointer', color: '#9e9e9e' }} titleAccess="Remove solution type" onClick={() => removeBlock(block)} />
                  </div>

                  {block.rows.map(row => (
                    <div key={row.id} style={styles.row}>
                      <div style={styles.headerCell}>
                        {/* Optional headers can be changed (stored images too); never to a mandatory header */}
                        {row.header && row.header.isMandatory ? (
                          <span>
                            {row.header.headerName}
                            <span style={styles.mandatory}>*</span>
                          </span>
                        ) : (
                          <Select
                            clearable={false}
                            options={optionalHeaders.map(h => ({ value: h.headerName, label: h.headerName }))}
                            value={row.header ? row.header.headerName : ''}
                            onChange={opt =>
                              updateRow(block.id, row.id, {
                                header: optionalHeaders.find(h => h.headerName === (opt && opt.value)) || null,
                                dirty: true
                              })
                            }
                            placeholder="Select header"
                          />
                        )}
                      </div>
                      {renderFileCell(block, row)}
                      {(!row.header || !row.header.isMandatory || block.rows.filter(r => r.header === row.header).length > 1) && (
                        <DeleteIcon style={{ fontSize: 18, cursor: 'pointer', color: '#9e9e9e' }} titleAccess="Remove" onClick={() => removeRow(block.id, row)} />
                      )}
                    </div>
                  ))}

                  {showError && problems.stillMissing.length > 0 && (
                    <div style={styles.error}>Missing: {problems.stillMissing.join(', ')}</div>
                  )}
                  {showError && problems.noImage && <div style={styles.error}>Upload at least one image.</div>}
                  {!strict && problems.stillMissing.length > 0 && (
                    <div style={styles.warning}>
                      <WarningIcon style={{ fontSize: 16 }} />
                      Mandatory images not uploaded yet: {problems.stillMissing.join(', ')}
                    </div>
                  )}

                  <Button size="small" color="primary" onClick={() => addOptionalRow(block)} style={{ marginTop: 6 }}>
                    <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
                    Add more images
                  </Button>
                </div>
              )
            })}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: '0 0 50%' }}>
                <Select
                  options={solutionSelectOptions}
                  value={newSolution}
                  onChange={opt => setNewSolution(opt ? opt.value : '')}
                  placeholder="Select solution type"
                />
              </div>
              <Button variant="outlined" color="primary" disabled={!newSolution} onClick={addSolutionBlock}>
                <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
                Add solution type
              </Button>
            </div>

            {unassigned.length > 0 && (
              <div style={styles.block}>
                <div style={styles.blockHead}>
                  <strong>Unassigned images ({unassigned.length})</strong>
                </div>
                <div style={{ ...styles.muted, marginBottom: 6 }}>
                  These stored images have no recognised solution type or header. They are kept as they are unless you assign them.
                </div>
                {unassigned.map(item => {
                  const block = blocks.find(b => b.solution === item.solution)
                  // mandatory headers that already have an image are not offered again
                  const headerOptions = block
                    ? assignableHeaders(block).map(h => ({ value: h.headerName, label: h.isMandatory ? `${h.headerName} *` : h.headerName }))
                    : []
                  const headerStillFree = headerOptions.some(o => o.value === item.headerName)
                  return (
                    <div key={item.id} style={styles.row}>
                      <div style={{ ...styles.fileCell, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={styles.fileName} title={item.entry.fileName}>
                          {item.entry.fileName}
                        </span>
                        <VisibilityIcon style={{ fontSize: 18, cursor: 'pointer', color: '#1976d2' }} titleAccess="View" onClick={() => handleView(item.entry.fileName)} />
                      </div>
                      <div style={{ flex: '0 0 22%' }}>
                        <Select
                          clearable={false}
                          options={blocks.map(b => ({ value: b.solution, label: solutionLabel(b.solution, solutionNames) }))}
                          value={item.solution}
                          onChange={opt =>
                            setUnassigned(prev => prev.map(u => (u.id !== item.id ? u : { ...u, solution: opt ? opt.value : '', headerName: '' })))
                          }
                          placeholder="Solution"
                        />
                      </div>
                      <div style={{ flex: '0 0 26%' }}>
                        <Select
                          clearable={false}
                          disabled={!block}
                          options={headerOptions}
                          value={headerStillFree ? item.headerName : ''}
                          onChange={opt => setUnassigned(prev => prev.map(u => (u.id !== item.id ? u : { ...u, headerName: opt ? opt.value : '' })))}
                          placeholder="Header"
                        />
                      </div>
                      <Button size="small" color="primary" disabled={!item.solution || !headerStillFree} onClick={() => assignUnassigned(item)}>
                        Assign
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}

            {message && <div style={styles.error}>{message}</div>}
          </React.Fragment>
        )}
      </DialogContent>
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg">
        <DialogTitle style={{ paddingRight: 56 }}>
          <span style={{ fontSize: 14, wordBreak: 'break-all' }}>{preview ? preview.fileName : ''}</span>
          <CloseIcon
            titleAccess="Close"
            onClick={() => setPreview(null)}
            style={{ position: 'absolute', right: 16, top: 16, cursor: 'pointer', color: '#616161' }}
          />
        </DialogTitle>
        <DialogContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 320, minHeight: 240 }}>
          {preview && preview.loading && <CircularProgress />}
          {preview && preview.error && <span style={styles.error}>{preview.error}</span>}
          {preview && preview.url && /\.pdf$/i.test(preview.fileName) && (
            <iframe title={preview.fileName} src={preview.url} style={{ width: '80vw', height: '75vh', border: 0 }} />
          )}
          {preview && preview.url && !/\.pdf$/i.test(preview.fileName) && (
            <img
              src={preview.url}
              alt={preview.fileName}
              style={{ maxWidth: '85vw', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
              onError={() => setPreview(prev => (prev ? { ...prev, url: '', error: 'Image could not be loaded' } : prev))}
            />
          )}
        </DialogContent>
      </Dialog>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={loading || isUploading} onClick={handleSave}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InstallationImagesModal



// WEBPACK FOOTER //
// ./src/components/Modules/VehicleRegistartion/InstallationImagesModal.jsx